import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import AccessRestricted from '../../components/AccessRestricted';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatTimestamp } from '../../utils/helpers';
import { hrDashboardApi } from '../../utils/hr-dashboard-api';
import type {
  HrDashboardData,
  HrFilters,
} from '../../types/hr-dashboard.types';
import { HrFilterBar } from './components/FilterBar';
import { HrStatusPill } from './components/StatusPill';
import { EMPTY_FILTERS, filterPeople, filterSummary } from './hr-dashboard-utils';
import { OverviewSection } from './sections/OverviewSection';
import { HeadcountSection } from './sections/HeadcountSection';
import { DemographicsSection } from './sections/DemographicsSection';
import { DiversitySection } from './sections/DiversitySection';
import { RecruitmentSection } from './sections/RecruitmentSection';
import { LeaveSection } from './sections/LeaveSection';
import { QualificationsSection } from './sections/QualificationsSection';
import { PoliciesSection } from './sections/PoliciesSection';
import { LearningSection } from './sections/LearningSection';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'headcount', label: 'Headcount' },
  { id: 'demographics', label: 'Demographics' },
  { id: 'diversity', label: 'Diversity' },
  { id: 'recruitment', label: 'Recruitment' },
  { id: 'leave', label: 'Leave balances' },
  { id: 'qualifications', label: 'Qualifications' },
  { id: 'policies', label: 'Policy acknowledgements' },
  { id: 'learning', label: 'Learning' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

const isSection = (value: string | null): value is SectionId =>
  SECTIONS.some((section) => section.id === value);

export default function HRDashboardPage() {
  const ability = useAbility(AbilityContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const canRead = ability.can(ACTIONS.READ, RESOURCES.HR_DASHBOARD);
  const canExport = ability.can(ACTIONS.EXPORT, RESOURCES.HR_DASHBOARD);

  const [data, setData] = useState<HrDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<HrFilters>(EMPTY_FILTERS);

  const sectionParam = searchParams.get('section');
  const section: SectionId = isSection(sectionParam) ? sectionParam : 'overview';

  const load = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const response = await hrDashboardApi.get(force);
      setData(response.data);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : 'Failed to load the HR Dashboard.';
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) load();
    else setLoading(false);
  }, [canRead, load]);

  const filteredPeople = useMemo(
    () => (data ? filterPeople(data.people, filters) : []),
    [data, filters]
  );

  const setFilter = (key: keyof HrFilters, values: string[]) =>
    setFilters((current) => ({ ...current, [key]: values }));

  const toggleDept = (department: string) =>
    setFilters((current) => ({
      ...current,
      dept: current.dept.includes(department)
        ? current.dept.filter((entry) => entry !== department)
        : [...current.dept, department],
    }));

  const checkExport = async (format: 'csv' | 'xlsx' = 'csv') => {
    if (!canExport) return false;
    try {
      await hrDashboardApi.checkExport({
        view: section,
        format,
        filters: filterSummary(filters),
      });
      return true;
    } catch (exportError) {
      toast.error(
        exportError instanceof Error ? exportError.message : 'Export is not permitted.'
      );
      return false;
    }
  };

  const changeSection = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('section', value);
    setSearchParams(next, { replace: true });
  };

  if (!canRead) return <AccessRestricted />;

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-4">
        <div className="h-10 w-64 animate-pulse rounded-md bg-muted" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="h-28 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full max-w-[1400px]">
        <Card>
          <CardContent className="space-y-3 py-8 text-center">
            <p className="font-medium text-foreground">The HR Dashboard could not be loaded.</p>
            <p className="text-sm text-muted-foreground">{error || 'No data was returned.'}</p>
            <Button onClick={() => load(true)}>Try again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sharedProps = {
    data,
    people: filteredPeople,
    filters,
    asOf: data.meta.asOf,
    canExport,
    checkExport,
    toggleDept,
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-border bg-card shadow-sm">
            <BarChart3 className="h-5 w-5 text-foreground" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">HR Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Live people, compliance and recruitment metrics · active staff only
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <HrStatusPill tone="link">Live</HrStatusPill>
              <span>as at {formatTimestamp(data.meta.generatedAt)}</span>
            </div>
          </div>
        </div>

        <Button variant="outline" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <HrFilterBar
        people={data.people}
        filters={filters}
        onChange={setFilter}
        onClear={() => setFilters(EMPTY_FILTERS)}
        resultCount={filteredPeople.length}
      />

      <Tabs value={section} onValueChange={changeSection}>
        <TabsList className="w-full justify-start overflow-x-auto p-1">
          {SECTIONS.map((item) => (
            <TabsTrigger key={item.id} value={item.id} className="flex-none px-3">
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {section === 'overview' ? <OverviewSection {...sharedProps} /> : null}
      {section === 'headcount' ? <HeadcountSection {...sharedProps} /> : null}
      {section === 'demographics' ? <DemographicsSection {...sharedProps} /> : null}
      {section === 'diversity' ? <DiversitySection {...sharedProps} /> : null}
      {section === 'recruitment' ? <RecruitmentSection {...sharedProps} /> : null}
      {section === 'leave' ? <LeaveSection {...sharedProps} /> : null}
      {section === 'qualifications' ? <QualificationsSection {...sharedProps} /> : null}
      {section === 'policies' ? <PoliciesSection {...sharedProps} /> : null}
      {section === 'learning' ? <LearningSection {...sharedProps} /> : null}
    </div>
  );
}
